<template>
  <v-dialog v-model="dialog" max-width="1200px" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Revisión de Documento</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" lg="6">
              <v-select
                v-model="formulario.estadoRevision"
                :items="estadosRevision"
                label="Estado de Revisión"
                :rules="[rules.required]"
                outlined
                dense></v-select>

              <v-textarea
                v-model="formulario.observaciones"
                label="Observaciones"
                :rules="observacionesRules"
                :counter="500"
                maxlength="500"
                outlined
                rows="4"></v-textarea>

              <v-file-input
                v-model="archivoAdjunto"
                label="Respaldo (PDF o imagen)"
                accept="application/pdf,image/*"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
                :rules="archivoAdjuntoRules"
                outlined
                dense
                clearable
                show-size
                @change="onCambioArchivo"></v-file-input>
            </v-col>

            <v-col cols="12" lg="6">
              <div
                v-if="!previsualizacion"
                class="d-flex align-center justify-center"
                style="height: 340px; border: 1px dashed #ccc">
                <span class="text-subtitle-1 text-center">Previsualización del respaldo</span>
              </div>

              <template v-else>
                <ImagenPreview v-if="esImagen" :src="previsualizacion" nombre-archivo="Respaldo" />
                <PdfPreview v-else-if="esPDF" :src="previsualizacion" nombre-archivo="Respaldo" />
              </template>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="cerrar"> Cancelar </v-btn>
        <v-btn color="primary" :disabled="!valid" @click="mostradoDialogoConfirmacion = true"> Guardar </v-btn>
      </v-card-actions>
    </v-card>

    <ConfirmacionAccion
      v-model="mostradoDialogoConfirmacion"
      v-bind="confirmacion"
      @aceptar="() => confirmacion.aceptar()" />
  </v-dialog>
</template>

<script>
import documentosPersonasService from '../../comisiones/services/documentos-personas.service';
import { ESTADOS_REVISIONES_ARRAY, ESTADOS_TIPO_DOCUMENTO_PERSONA } from '../../comisiones/utils/constantes';
import ConfirmacionAccion from './ConfirmacionAccion.vue';
import ImagenPreview from './ImagenPreview.vue';
import PdfPreview from './PdfPreview.vue';

export default {
  name: 'FormularioRevision',

  components: {
    ConfirmacionAccion,
    ImagenPreview,
    PdfPreview,
  },

  data() {
    return {
      dialog: false,
      valid: false,
      documentoPersonaId: null,
      formulario: {
        estadoRevision: null,
        observaciones: null,
        archivoBase64: null,
      },
      archivoAdjunto: null,
      previsualizacion: null,
      esImagen: false,
      esPDF: false,
      estadosRevision: ESTADOS_REVISIONES_ARRAY,
      rules: {
        required: (v) => !!v || 'Este campo es requerido',
      },
      mostradoDialogoConfirmacion: false,
      confirmacion: {
        titulo: 'Confirmar Revisión',
        mensaje: '¿Está seguro de guardar la revisión del documento?',
        textoAceptar: 'Sí, guardar',
        textoCancelar: 'Cancelar',
        realizandoAccion: false,
        aceptar: () => {
          this.guardar();
        },
      },
      archivoAdjuntoRules: [
        (v) => !v || v.size <= 10 * 1024 * 1024 || 'El archivo debe ser menor a 10MB',
        (v) =>
          !v ||
          ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(v.type) ||
          'Solo se permiten archivos PDF o imágenes (JPEG, PNG)',
      ],
    };
  },

  computed: {
    observacionesRules() {
      const rules = [(v) => !v || v.length <= 500 || 'Máximo 500 caracteres'];

      if (this.formulario.estadoRevision === ESTADOS_TIPO_DOCUMENTO_PERSONA.RECHAZADO) {
        rules.unshift((v) => !!v || 'Las observaciones son obligatorias cuando se rechaza');
      }

      return rules;
    },
  },

  methods: {
    abrir(documentoPersonaId) {
      this.dialog = true;
      this.documentoPersonaId = documentoPersonaId;
      this.formulario = {
        estadoRevision: null,
        observaciones: null,
        archivoBase64: null,
      };
      this.archivoAdjunto = null;
      this.previsualizacion = null;
      this.esImagen = false;
      this.esPDF = false;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    cerrar() {
      this.dialog = false;
      this.$refs.form.reset();
      this.archivoAdjunto = null;
      this.previsualizacion = null;
      this.esImagen = false;
      this.esPDF = false;
      this.formulario.archivoBase64 = null;
    },

    onCambioArchivo(archivo) {
      this.previsualizacion = null;
      this.esImagen = false;
      this.esPDF = false;
      this.formulario.archivoBase64 = null;

      if (!archivo) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        const blob = new Blob([archivo], { type: archivo.type });

        this.formulario.archivoBase64 = result.split(',')[1];
        this.previsualizacion = URL.createObjectURL(blob);

        if (archivo.type.startsWith('image/')) {
          this.esImagen = true;
        } else if (archivo.type === 'application/pdf') {
          this.esPDF = true;
        }
      };
      reader.readAsDataURL(archivo);
    },
    async guardar() {
      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        const response = await documentosPersonasService.revisar(this.documentoPersonaId, this.formulario);
        const data = response.data;
        const mensaje = data.mensaje || 'Documento revisado correctamente';

        this.$notify({
          group: 'notifications',
          title: 'Documento revisado',
          text: mensaje,
          type: 'success',
        });

        this.$emit('documento-revisado', data.datos);
        this.cerrar();
      } catch (err) {
        const response = err?.response;
        const data = response?.data;
        const errores = data?.errores || 'Error al revisar el documento';

        window.console.error('Error al revisar documento:', err);

        const mensajesErrores = Object.values(errores).flat().join(', ');

        this.$notify({
          group: 'notifications',
          title: 'Error al revisar documento',
          text: mensajesErrores,
          type: 'error',
        });
      }
    },
  },
};
</script>